import {
  Box,
  Button,
  IconButton,
  Popover,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ExoDrawer from '../exo/ExoDrawer';
import { createElement, useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ModuleLayout from './ModuleLayout';
import useFetch from '../../api/useFetch';
import { API_GET, DBRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import { renderHours } from '../../scenes/time-tracking/worktime';
import { timeTypeMap } from '../../scenes/time-manager/user-entries/timeline/timeTypeMap';
import { useTimeTracking } from '../time-tracking/AppTimeProvider';
import DateLabel from '../label/DateLabel';

const VIEW_PERIODS = {
  week: { label: 'Week', unit: 'week', startOf: 'isoWeek', endOf: 'isoWeek' },
  month: { label: 'Month', unit: 'month', startOf: 'month', endOf: 'month' },
};

const DashboardTimeWeek = ({ colSpan = 1, rowSpan = 1, minHeight }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const { activeProject, secondsIn } = useTimeTracking();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [viewPeriod, setViewPeriod] = useState('week');
  const [activeMoment, setActiveMoment] = useState(moment());
  const [times, setTimes] = useState(null);
  const [maxValue, setMaxValue] = useState(null);

  // Popover state
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  const handleDayClick = (event, time) => {
    setPopoverAnchor(event.currentTarget);
    setSelectedTime(time);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setSelectedTime(null);
  };

  const periodConfig = VIEW_PERIODS[viewPeriod];

  // Calculate date boundaries based on view period
  const startDate = activeMoment.clone().startOf(periodConfig.startOf).format('YYYY-MM-DD');
  const endDate = activeMoment.clone().endOf(periodConfig.endOf).format('YYYY-MM-DD');

  // Initial fetch
  const { apiData, setApiData } = useFetch(
    `time-tracking/overview/own?period=day&startDate=${startDate}&endDate=${endDate}`
  );

  useEffect(() => {
    if (apiData?.data?.times) {
      setTimes(apiData.data.times);
      calculateMaxValue(apiData.data.times);
    }
  }, [apiData]);

  // Refetch when view period changes
  const handleViewPeriodChange = (newPeriod) => {
    setViewPeriod(newPeriod);
    setTimes(null);
    const newConfig = VIEW_PERIODS[newPeriod];
    const newStart = activeMoment.clone().startOf(newConfig.startOf).format('YYYY-MM-DD');
    const newEnd = activeMoment.clone().endOf(newConfig.endOf).format('YYYY-MM-DD');

    DBRequest({
      config,
      path: `time-tracking/overview/own?period=day&startDate=${newStart}&endDate=${newEnd}`,
      method: API_GET,
      onResponse: (data) => {
        setTimes(data.data.times);
        calculateMaxValue(data.data.times);
      },
    });
  };

  function calculateMaxValue(timesData) {
    let maxTargetTimeSeconds = 0;
    let maxWorkedTimeSeconds = 0;
    timesData.forEach((time) => {
      if (time.targetTimeSeconds > maxTargetTimeSeconds) {
        maxTargetTimeSeconds = time.targetTimeSeconds;
      }
      // Use the same calculation as in DayElement (worked percentage)
      const workedPercentage = time.percentages?.worked ?? 1;
      const workedTimeSeconds = parseInt(time.totalTimeSeconds * workedPercentage);
      if (workedTimeSeconds > maxWorkedTimeSeconds) {
        maxWorkedTimeSeconds = workedTimeSeconds;
      }
    });
    const biggestTimeSeconds =
      maxTargetTimeSeconds > maxWorkedTimeSeconds
        ? maxTargetTimeSeconds
        : maxWorkedTimeSeconds;
    setMaxValue(biggestTimeSeconds);
  }

  // Navigation handlers
  const handleSubtract = () => {
    const newMoment = activeMoment.clone().subtract(1, periodConfig.unit);
    setActiveMoment(newMoment);
    fetchData(newMoment);
  };

  const handleAdd = () => {
    const newMoment = activeMoment.clone().add(1, periodConfig.unit);
    setActiveMoment(newMoment);
    fetchData(newMoment);
  };

  const fetchData = (targetMoment) => {
    setTimes(null);
    const newStart = targetMoment.clone().startOf(periodConfig.startOf).format('YYYY-MM-DD');
    const newEnd = targetMoment.clone().endOf(periodConfig.endOf).format('YYYY-MM-DD');

    DBRequest({
      config,
      path: `time-tracking/overview/own?period=day&startDate=${newStart}&endDate=${newEnd}`,
      method: API_GET,
      onResponse: (data) => {
        setTimes(data.data.times);
        calculateMaxValue(data.data.times);
      },
    });
  };

  // Check if current period
  const isCurrent = viewPeriod === 'week'
    ? activeMoment.isoWeek() === moment().isoWeek() && activeMoment.year() === moment().year()
    : activeMoment.isSame(moment(), 'month');

  // Calculate total worked time for the period
  const calculateTotalWorkedTime = () => {
    if (!times) return { worked: 0, target: 0 };

    let totalWorkedSeconds = 0;
    let totalTargetSeconds = 0;

    times.forEach((time) => {
      const workedPercentage = time.percentages?.worked ?? 1;
      totalWorkedSeconds += parseInt(time.totalTimeSeconds * workedPercentage);
      totalTargetSeconds += time.targetTimeSeconds || 0;
    });

    // Add live tracking time if currently working
    const todayTime = times.find(t => t.date === moment().format('YYYY-MM-DD'));
    if (activeProject && todayTime) {
      totalWorkedSeconds += secondsIn;
    }

    return { worked: totalWorkedSeconds, target: totalTargetSeconds };
  };

  const totalTime = calculateTotalWorkedTime();

  const weekDays = [
    t('Monday'),
    t('Tuesday'),
    t('Wednesday'),
    t('Thursday'),
    t('Friday'),
    t('Saturday'),
    t('Sunday'),
  ];

  // Generate header label based on view period
  const getHeaderLabel = () => {
    if (viewPeriod === 'week') {
      return `KW${activeMoment.isoWeek()}`;
    } else {
      return t(activeMoment.format('MMMM'));
    }
  };

  // Get skeleton count based on view period
  const getSkeletonCount = () => {
    if (viewPeriod === 'week') return 7;
    return 35; // Approximate for month
  };

  // Prepare times with padding for month view (to start on Monday)
  const getPaddedTimes = () => {
    if (!times || viewPeriod !== 'month') return times;

    // Get the first day of the month and find which weekday it is (1=Monday, 7=Sunday)
    const firstDayOfMonth = moment(times[0]?.date);
    const firstDayWeekday = firstDayOfMonth.isoWeekday(); // 1=Monday, 7=Sunday

    // Add placeholder cells before the first day
    const placeholders = [];
    for (let i = 1; i < firstDayWeekday; i++) {
      placeholders.push({ placeholder: true, date: `placeholder-${i}` });
    }

    return [...placeholders, ...times];
  };

  const displayTimes = getPaddedTimes();

  return (
    <ModuleLayout colSpan={colSpan} rowSpan={rowSpan} minHeight={minHeight}>
      <Box className="flex flex-col h-full w-full">
        {/* Header with navigation */}
        <Box className="flex items-center justify-between mb-2">
          <IconButton
            onClick={handleSubtract}
            size="small"
            sx={{
              minWidth: isMobile ? '40px' : 'auto',
              minHeight: isMobile ? '40px' : 'auto',
            }}
          >
            <ArrowLeftIcon />
          </IconButton>
          <Box className="flex flex-col items-center">
            <Typography
              fontSize={isMobile ? 14 : 16}
              fontWeight={600}
              className="opacity-70"
            >
              {getHeaderLabel()}{' '}
              {isCurrent && (
                <span className="opacity-50">({t('current')})</span>
              )}
            </Typography>
            <Typography fontSize={isMobile ? 11 : 12} className="opacity-50">
              <DateLabel date={startDate} /> - <DateLabel date={endDate} />
            </Typography>
            {times && (
              <Box className="flex items-center gap-2 mt-1">
                <Typography
                  fontSize={isMobile ? 12 : 14}
                  fontWeight={600}
                  className="monospace"
                  sx={{ color: colors.info }}
                >
                  {renderHours(totalTime.worked / 60)}h
                </Typography>
                <Typography fontSize={isMobile ? 11 : 12} className="opacity-50">
                  / {renderHours(totalTime.target / 60)}h {t('target')}
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton
            onClick={handleAdd}
            size="small"
            sx={{
              minWidth: isMobile ? '40px' : 'auto',
              minHeight: isMobile ? '40px' : 'auto',
            }}
          >
            <ArrowRightIcon />
          </IconButton>
        </Box>

        {/* Time grid */}
        <Box
          className={`grid gap-1 flex-1 ${viewPeriod === 'month' ? 'overflow-y-auto' : ''}`}
          sx={{
            gridTemplateColumns: 'repeat(7, 1fr)',
          }}
        >
          {/* Time elements */}
          {displayTimes ? (
            displayTimes.map((time, index) =>
              time.placeholder ? (
                <Box key={time.date} className="opacity-20" />
              ) : (
                <DayElement
                  key={time.date}
                  time={time}
                  maxValue={maxValue}
                  dayLabel={weekDays[moment(time.date).isoWeekday() - 1]?.substring(0, 2)}
                  activeTime={
                    moment(time.date).format('YYYY-MM-DD') ===
                      moment().format('YYYY-MM-DD') && activeProject
                      ? { timeStart: activeProject.clockInTime }
                      : null
                  }
                  secondsIn={secondsIn}
                  compact={viewPeriod === 'month'}
                  onClick={(e) => handleDayClick(e, time)}
                />
              )
            )
          ) : (
            // Skeleton loading
            Array(getSkeletonCount())
              .fill(null)
              .map((_, index) => (
                <Skeleton
                  key={index}
                  sx={{
                    transform: 'unset',
                    borderRadius: '6px',
                    height: viewPeriod === 'month' ? '60px' : '100%',
                  }}
                />
              ))
          )}
        </Box>

        {/* Day detail popover/drawer */}
        {isMobile ? (
          <ExoDrawer
            open={Boolean(selectedTime)}
            onClose={handlePopoverClose}
            anchor="bottom"
          >
            {selectedTime && <DayDetailPopover time={selectedTime} isMobile={isMobile} />}
          </ExoDrawer>
        ) : (
          <Popover
            open={Boolean(popoverAnchor)}
            anchorEl={popoverAnchor}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            {selectedTime && <DayDetailPopover time={selectedTime} isMobile={isMobile} />}
          </Popover>
        )}

        {/* Period toggle buttons */}
        <Box className="flex justify-center gap-1 mt-2">
          {Object.entries(VIEW_PERIODS).map(([key, config]) => (
            <Button
              key={key}
              size="small"
              onClick={() => handleViewPeriodChange(key)}
              sx={{
                minWidth: isMobile ? '60px' : 'auto',
                minHeight: isMobile ? '36px' : 'auto',
                px: isMobile ? 1.5 : 1,
                py: isMobile ? 0.5 : 0.25,
                fontSize: isMobile ? 11 : 10,
                color: viewPeriod === key ? colors.info : colors.grey[500],
                bgcolor: viewPeriod === key ? colors.info + '20' : 'transparent',
                '&:hover': {
                  bgcolor: viewPeriod === key ? colors.info + '30' : colors.grey[800] + '20',
                },
              }}
            >
              {t(config.label)}
            </Button>
          ))}
        </Box>
      </Box>
    </ModuleLayout>
  );
};

const DayElement = ({ time, maxValue, activeTime, secondsIn, dayLabel, compact = false, onClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  // Calculate worked time based on percentage (0 if vacation/sick/etc.)
  const workedPercentage = time.percentages?.worked ?? 1;
  const baseWorkedSeconds = parseInt(time.totalTimeSeconds * workedPercentage);

  const [timeInSeconds, setTimeInSeconds] = useState(baseWorkedSeconds);

  const isToday = time.date === moment().format('YYYY-MM-DD');

  useEffect(() => {
    if (activeTime) {
      // Update with live tracking - add live seconds to base worked time
      const newTotalTimeSeconds = baseWorkedSeconds + secondsIn;
      setTimeInSeconds(newTotalTimeSeconds);
    } else {
      setTimeInSeconds(baseWorkedSeconds);
    }
  }, [activeTime, secondsIn, baseWorkedSeconds]);

  // Calculate bar height as percentage
  const percent = maxValue > 0 && timeInSeconds ? timeInSeconds / maxValue : 0;

  // Calculate target line position as percentage
  const targetPercent = maxValue > 0 ? (time.targetTimeSeconds / maxValue) || 0 : 0;

  // Determine time type
  function calculateType(t) {
    if (!t.percentages) return 'worked';
    let newType = null;
    const { percentages } = t;
    const keys = Object.keys(percentages);
    keys.forEach((key) => {
      if (percentages[key] === 0) return;
      if (newType) {
        newType = 'mixed';
      } else {
        newType = key;
      }
    });
    return newType || 'worked';
  }

  const type = calculateType(time);

  const color = activeTime
    ? colors.success // Currently working
    : percent > 0
    ? colors.info // Has worked
    : colors.selected; // Wasn't working

  return (
    <Box
      onClick={onClick}
      className={`flex flex-col rounded-md ${compact ? 'p-0.5' : 'p-1'} cursor-pointer`}
      sx={{
        backgroundColor: isToday ? colors.info + '15' : 'transparent',
        border: isToday ? `1px solid ${colors.info}40` : '1px solid transparent',
        minHeight: compact ? '50px' : 'auto',
        height: compact ? '50px' : '100%',
        '&:hover': {
          bgcolor: colors.info + '10',
        },
      }}
    >
      {/* Day label + number */}
      <Box className="flex justify-between items-center px-1">
        {dayLabel && (
          <Typography
            fontSize={compact ? 8 : 10}
            fontWeight={500}
            className="opacity-60"
          >
            {dayLabel}
          </Typography>
        )}
        <Typography
          fontSize={compact ? 9 : 11}
          fontWeight={isToday ? 700 : 500}
          sx={{
            color: isToday ? colors.info : 'inherit',
            marginLeft: dayLabel ? 0 : 'auto',
          }}
        >
          {moment(time.date).format('D')}
        </Typography>
      </Box>

      {/* Bar container */}
      <Box className="flex-1 flex flex-col justify-end relative">
        {/* Target area (shaded background up to target line) */}
        {!isNaN(targetPercent) && targetPercent > 0 && (
          <Box
            sx={{
              borderTop: `1px dashed ${colors.selected}`,
              height: `${targetPercent * 100}%`,
              bgcolor: colors.selected + '30',
            }}
            className="w-full absolute bottom-0 left-0 right-0 z-0"
          />
        )}

        {/* Time bar */}
        <Box
          sx={{
            bgcolor: color + '80',
            height: `${percent * 100}%`,
            border: percent > 0 ? `1px solid ${color}` : 'none',
            transition: 'height 0.6s ease',
          }}
          className="rounded-t-md z-10"
        />

        {/* Type icon overlay */}
        {type !== 'worked' && timeTypeMap[type] && (
          <Box
            className="absolute inset-0 flex items-center justify-center"
            sx={{
              backgroundImage: `linear-gradient(0deg, ${
                colors[timeTypeMap[type].color] + '40'
              }, ${colors[timeTypeMap[type].color] + '00'})`,
            }}
          >
            {timeTypeMap[type].Icon && (
              <Tooltip title={t(timeTypeMap[type].label)}>
                {createElement(timeTypeMap[type].Icon, {
                  sx: {
                    color: colors[timeTypeMap[type].color] + '80',
                    fontSize: compact ? 16 : 24,
                  },
                })}
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* Hours label */}
      {!compact && (
        <Typography
          fontSize={9}
          textAlign="center"
          className="monospace opacity-80"
        >
          {timeInSeconds > 0 ? `${renderHours(timeInSeconds / 60)}h` : '--'}
        </Typography>
      )}
    </Box>
  );
};

// Popover content for day details
const DayDetailPopover = ({ time, isMobile }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const {
    holiday = 0,
    sick = 0,
    vacation = 0,
    worked = 0,
    unpaidVacation = 0,
    maternityLeave = 0,
    paternityLeave = 0,
    parentalLeave = 0,
    childSickLeave = 0,
    careLeave = 0,
    educationalLeave = 0,
    timeCompensation = 0,
  } = time?.percentages || {};

  // Calculate deviation
  const nonWorkedPercentage =
    vacation + holiday + sick + unpaidVacation + maternityLeave +
    paternityLeave + parentalLeave + childSickLeave + careLeave +
    educationalLeave + timeCompensation;

  const deviation =
    time?.totalTimeSeconds -
    (time?.targetTimeSeconds + time?.totalTimeSeconds * nonWorkedPercentage);

  // Get time items for display
  const timeItems = [];
  if (time?.percentages) {
    const keys = Object.keys(time.percentages);
    keys.forEach((key) => {
      if (time.percentages[key] === 0) return;
      if (timeTypeMap[key]) {
        timeItems.push({
          ...timeTypeMap[key],
          percent: time.percentages[key],
          seconds: time.totalTimeSeconds * time.percentages[key],
        });
      }
    });
  }

  return (
    <Box
      className="p-4"
      sx={{
        bgcolor: colors.bgPaper,
        minWidth: isMobile ? 'auto' : '280px',
        width: isMobile ? '100%' : 'auto',
      }}
    >
      {/* Header */}
      <Box className="flex justify-between items-center mb-3">
        <Typography fontWeight={600}>
          {t(moment(time.date).format('dddd'))},{' '}
          <DateLabel date={time.date} />
        </Typography>
      </Box>

      {/* Deviation */}
      {deviation !== 0 && !isNaN(deviation) && (
        <Box
          className="flex items-center justify-between p-2 rounded mb-3"
          sx={{
            bgcolor: deviation > 0 ? colors.success + '15' : colors.error + '15',
            border: `1px solid ${deviation > 0 ? colors.success + '40' : colors.error + '40'}`,
          }}
        >
          <Typography fontSize={13} sx={{ color: deviation > 0 ? colors.success : colors.error }}>
            {t('deviation')}
          </Typography>
          <Typography
            fontWeight={600}
            className="monospace"
            fontSize={14}
            sx={{ color: deviation > 0 ? colors.success : colors.error }}
          >
            {deviation > 0 ? '+' : '-'}{renderHours(Math.abs(deviation) / 60)}h
          </Typography>
        </Box>
      )}

      {/* Time breakdown */}
      <Box className="flex flex-col gap-2">
        {timeItems.map((item, index) => (
          <Box
            key={index}
            className="flex items-center justify-between p-2 rounded"
            sx={{ bgcolor: colors[item.color] + '15' }}
          >
            <Box className="flex items-center gap-2">
              {item.Icon && createElement(item.Icon, {
                sx: { color: colors[item.color], fontSize: 20 },
              })}
              <Typography fontSize={13}>
                {t(item.label)}
                {item.percent < 1 && (
                  <span className="opacity-60 ml-1">
                    ({Math.round(item.percent * 100)}%)
                  </span>
                )}
              </Typography>
            </Box>
            <Typography fontWeight={600} className="monospace" fontSize={13}>
              {renderHours(item.seconds / 60)}h
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Target time */}
      <Box className="flex justify-between mt-3 pt-2 border-t opacity-60">
        <Typography fontSize={12}>{t('target')}:</Typography>
        <Typography fontSize={12} className="monospace">
          {renderHours((time.targetTimeSeconds || 0) / 60)}h
        </Typography>
      </Box>
    </Box>
  );
};

export default DashboardTimeWeek;
